import { useState } from 'react'
import { useNavigation } from '@react-navigation/native'
import Toast from 'react-native-toast-message'
import { useForgotPassword as useForgotPasswordMutation } from '@hooks'
import type { GenericNavigationProp } from '@navigation'
import { useLanguage } from '@context'
import { isUncodedBadRequest, isValidEmail, normalizeEmail, translateApiError } from '@utils'

interface UseForgotPasswordResult {
  email: string
  setEmail: React.Dispatch<React.SetStateAction<string>>
  handleForgotPassword: () => void
  isPending: boolean
  error: string
}

export const useForgotPassword = (): UseForgotPasswordResult => {
  const { t } = useLanguage()
  const navigation = useNavigation<GenericNavigationProp>()
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')

  const { mutate: forgotPasswordMutation, isPending } = useForgotPasswordMutation()

  const handleForgotPassword = () => {
    setError('')

    // Same clean-up as the login and sign-up screens: no stray space, no capitals.
    const cleanedEmail = normalizeEmail(email ?? '')
    if (!cleanedEmail) {
      setError(t('forgotPassword.error.noEmail'))
      return
    }
    if (!isValidEmail(cleanedEmail)) {
      setError(t('errors.invalidEmail'))
      return
    }

    forgotPasswordMutation(
      { email: cleanedEmail },
      {
        onSuccess: () => {
          Toast.show({
            type: 'success',
            text2: t('forgotPassword.success'),
          })
          navigation.goBack()
        },
        onError: (err) => {
          // The only field the API validates here is the e-mail.
          if (isUncodedBadRequest(err)) {
            setError(t('errors.invalidEmail'))
            return
          }
          const { message } = translateApiError(t, err)
          setError(message)
        },
      },
    )
  }

  return {
    email,
    setEmail,
    handleForgotPassword,
    isPending,
    error,
  }
}
